import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { PhoneCall, AlertCircle } from "lucide-react";

export default function ActivationGate({ children }) {
  const { user, client, refresh } = useAuth();
  
  // Refresh auth info when mounting to ensure we have the latest status
  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  // Admin and staff roles bypass this gate
  if (["super_admin", "staff"].includes(user?.role)) {
    return <>{children}</>;
  }

  // If no client data is loaded yet, wait for it
  if (!client) {
    return <>{children}</>;
  }

  // If client is active and paid, render normally
  if (client.isActive) {
    return <>{children}</>;
  }

  // If client is trial/unpaid, render dashboard with blocking overlay
  if (client.status === 'trial' || client.paymentStatus === 'trial' || client.paymentStatus === 'unpaid') {
    return (
      <div className="relative min-h-screen">
        {/* Render children normally underneath (but block interactions) */}
        <div className="pointer-events-none select-none opacity-40 blur-[2px] transition-all duration-300">
          {children}
        </div>

        {/* Blocking Overlay */}
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
            {/* Banner top */}
            <div className="bg-gradient-to-br from-brand-700 to-brand-500 px-6 py-8 text-center text-white">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-md">
                <AlertCircle className="h-8 w-8 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold">Activate your account</h2>
              <p className="mt-2 text-brand-50">
                Your dashboard is ready. Complete your payment to start using it.
              </p>
            </div>
            
            {/* Content block - swappable later for a "Pay Now" checkout */}
            <div className="px-6 py-8 text-center">
              <p className="mb-6 text-sm text-slate-600 leading-relaxed">
                We are currently processing payments manually. Please contact our support team to activate your plan and unlock all premium features.
              </p>
              
              <div className="flex flex-col items-center gap-3">
                <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-50 p-4 text-brand-700 border border-brand-100">
                  <PhoneCall className="h-5 w-5" />
                  <span className="text-lg font-bold">{client.contactNumber || "Support"}</span>
                </div>
                <p className="text-xs text-slate-400">Available Monday to Friday, 9am - 6pm</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For 'suspended' or 'expired', fallback to whatever normal handling exists
  // (the prompt said those already have dedicated pages, so we don't block them here)
  return <>{children}</>;
}
